import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FiCheckCircle, FiInfo, FiLoader, FiArrowLeft } from "react-icons/fi";
import { useApi } from "../../Common/Hooks/useApi";
import { isErr } from "../../Common/Utils/Result";
import { eventBus } from "../../Common/EventBus";
import { Notification } from "../../Components/Notifications";
import {
  Page,
  PaymentBanner,
  CreditsCard,
  CreditsLabel,
  CreditsAmount,
  CreditsDescription,
  PackCard,
  PackLeft,
  PackBadge,
  PackTitle,
  PackDescription,
  PackRight,
  PriceRow,
  PriceAmount,
  PriceCurrency,
  BuyButton,
  BackToChatLink,
} from "./Styles";

type ChatCredits = {
  unlimited: boolean;
  availableChatCredits: number;
};

export const BuyCredits = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { getChatCreditsEndpoint, getChatCreditPackPaymentUrlEndpoint } = useApi();
  const [credits, setCredits] = useState<ChatCredits | null>(null);
  const [isLoadingCredits, setIsLoadingCredits] = useState(true);
  const [isBuying, setIsBuying] = useState(false);

  const paymentStatus = searchParams.get("payment");

  const loadCredits = useCallback(async () => {
    setIsLoadingCredits(true);
    const response = await getChatCreditsEndpoint();
    if (!isErr(response)) {
      setCredits({
        unlimited: response.value.unlimited,
        availableChatCredits: response.value.availableChatCredits,
      });
    }
    setIsLoadingCredits(false);
  }, [getChatCreditsEndpoint]);

  useEffect(() => {
    loadCredits();
  }, [loadCredits]);

  const handleBuyPack = async () => {
    if (isBuying) return;
    setIsBuying(true);
    const response = await getChatCreditPackPaymentUrlEndpoint();
    if (isErr(response)) {
      eventBus.emit(
        "NOTIFICATION",
        Notification.buildNotification("error", t("pricing.buyCredits.notifications.buyPackFailed"))
      );
      setIsBuying(false);
      return;
    }
    window.location.href = response.value.paymentUrl;
  };

  return (
    <Page>
      {paymentStatus === "success" && (
        <PaymentBanner $variant="success">
          <FiCheckCircle size={18} />
          {t("pricing.buyCredits.paymentBanner.success")}
        </PaymentBanner>
      )}
      {paymentStatus === "cancelled" && (
        <PaymentBanner $variant="neutral">
          <FiInfo size={18} />
          {t("pricing.buyCredits.paymentBanner.cancelled")}
        </PaymentBanner>
      )}

      <CreditsCard>
        {isLoadingCredits ? (
          <>
            <CreditsLabel>{t("pricing.buyCredits.creditsCard.label")}</CreditsLabel>
            <CreditsAmount>—</CreditsAmount>
            <CreditsDescription>{t("pricing.buyCredits.creditsCard.loading")}</CreditsDescription>
          </>
        ) : credits?.unlimited ? (
          <>
            <CreditsLabel>{t("pricing.buyCredits.creditsCard.label")}</CreditsLabel>
            <CreditsAmount>{t("pricing.buyCredits.creditsCard.unlimitedAmount")}</CreditsAmount>
            <CreditsDescription>
              {t("pricing.buyCredits.creditsCard.unlimitedDescription")}
            </CreditsDescription>
          </>
        ) : credits ? (
          <>
            <CreditsLabel>{t("pricing.buyCredits.creditsCard.label")}</CreditsLabel>
            <CreditsAmount>{credits.availableChatCredits}</CreditsAmount>
            <CreditsDescription>
              {t("pricing.buyCredits.creditsCard.availableDescription")}
            </CreditsDescription>
          </>
        ) : (
          <>
            <CreditsLabel>{t("pricing.buyCredits.creditsCard.label")}</CreditsLabel>
            <CreditsAmount>—</CreditsAmount>
            <CreditsDescription>{t("pricing.buyCredits.creditsCard.loadErrorDescription")}</CreditsDescription>
          </>
        )}
      </CreditsCard>

      {!credits?.unlimited && (
        <PackCard>
          <PackLeft>
            <PackBadge>{t("pricing.buyCredits.packCard.badge")}</PackBadge>
            <PackTitle>{t("pricing.buyCredits.packCard.title")}</PackTitle>
            <PackDescription>
              {t("pricing.buyCredits.packCard.description")}
            </PackDescription>
          </PackLeft>
          <PackRight>
            <PriceRow>
              <PriceAmount>19</PriceAmount>
              <PriceCurrency>€</PriceCurrency>
            </PriceRow>
            <BuyButton onClick={handleBuyPack} disabled={isBuying}>
              {isBuying ? (
                <>
                  <FiLoader className="spin" size={15} /> {t("pricing.buyCredits.packCard.buyButtonLoading")}
                </>
              ) : (
                t("pricing.buyCredits.packCard.buyButton")
              )}
            </BuyButton>
          </PackRight>
        </PackCard>
      )}

      <BackToChatLink onClick={() => navigate("/chat")}>
        <FiArrowLeft size={13} />
        {t("pricing.buyCredits.backToChatLink")}
      </BackToChatLink>
    </Page>
  );
};
